import { useCallback, useEffect, useRef, useState } from 'react';
import { ApolloCache, ApolloError, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ContractTemplateDraftSampleDocument,
  ContractTemplateDraftSampleQuery,
  ContractTemplateDraftSampleQueryVariables,
  ContractTemplateEditorSourceQuery,
  useContractTemplateEditorSourceQuery,
  useContractTemplateListQuery,
  useCopyContractTemplateMutation,
  useStoreContractTemplateVersionMutation,
  useSwitchContractTemplateVersionMutation,
  useToggleContractTemplateCodeMutation,
} from 'core/graphql';
import { isPermissionError } from 'core/utils/isPermissionError';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import {
  IContractTemplateProblem,
  IContractTemplateToken,
  IContractTemplateVariable,
  templateEditorLanguage,
  tokenLabel,
} from './contractTemplateEditorModel';
import { IContractTemplate } from './useContractTemplates';
import { IEsignFieldError, toEsignErrors } from './useEsignMutations';
import { useEsignPermissions } from './useEsignPermissions';

/**
 * ТЗ 0107 — редактор шаблонов договоров: текст для визуального редактора,
 * образец несохранённого текста, версии кода и записи (только Admin —
 * решение владельца 14.10; остальные роли видят справочник как раньше).
 *
 * Скрытие кнопок — UX: каждую мутацию сервер закрывает той же ролью.
 */

export type IContractTemplateEditable = NonNullable<
  ContractTemplateEditorSourceQuery['contractTemplateEditable']
>;

/** Создавать и править шаблоны договоров может только Admin. */
export const useCanEditContractTemplates = (): boolean =>
  useEsignPermissions().isAdmin;

/**
 * После УДАЧНОЙ записи справочник, история и образцы перечитываются:
 * списки — поля с аргументами на ROOT_QUERY, выбрасываются целиком.
 *
 * Отказ (`success=false`, ошибки проверки) кэш не трогает: иначе
 * выброшенный запрос перерисовал бы открытые окна лоадером. Текст редактора
 * (`contractTemplateEditable`) не выбрасывается никогда: открытый редактор
 * читает его `network-only`, а после успеха закрывается сам.
 */
export const evictTemplateCachesOnSuccess = (
  cache: Pick<ApolloCache<unknown>, 'evict' | 'gc'>,
  { data }: { data?: unknown },
): void => {
  const payload = data
    ? (Object.values(data as Record<string, unknown>)[0] as
        | { success?: boolean | null }
        | null
        | undefined)
    : null;
  if (!payload?.success) return;
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'contractTemplates' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'contractTemplatePreview' });
  cache.gc();
};

const EMPTY_TOKENS: IContractTemplateToken[] = [];
const EMPTY_VARIABLES: IContractTemplateVariable[] = [];
const EMPTY_PROBLEMS: IContractTemplateProblem[] = [];
const EMPTY_VERSIONS: IContractTemplate[] = [];

/**
 * Тело шаблона для редактора. Подписи чипов — на языке интерфейса.
 * `network-only`: редактор всегда начинает с текущего текста версии.
 */
export const useContractTemplateEditable = (templateId: string | null) => {
  const { i18n } = useTranslation();
  const canEdit = useCanEditContractTemplates();
  const language = templateEditorLanguage(i18n.language);
  const {
    data,
    loading,
    error,
    refetch,
  } = useContractTemplateEditorSourceQuery({
    variables: { id: templateId || '', language },
    skip: !templateId || !canEdit,
    fetchPolicy: 'network-only',
  });
  const editable = data?.contractTemplateEditable ?? null;
  return {
    /** Нет прав (не Admin или отказ сервера) — не «не удалось загрузить». */
    forbidden: !canEdit || isPermissionError(error),
    editable,
    template: editable?.template ?? null,
    html: editable?.html ?? '',
    tokens: (editable?.tokens as IContractTemplateToken[]) ?? EMPTY_TOKENS,
    variables:
      (editable?.variables as IContractTemplateVariable[]) ?? EMPTY_VARIABLES,
    language,
    loading,
    failed: Boolean(error) || (!loading && Boolean(data) && !editable),
    refetch,
  };
};

/**
 * Образец несохранённого текста. `request(html)` — последний запрос
 * побеждает: ответ на устаревший текст (медленная сеть) не затирает свежий.
 * Не кэшируется: html каждый раз новый.
 */
export const useContractTemplateDraftSample = (templateId: string | null) => {
  const client = useApolloClient();
  const counter = useRef(0);
  const [state, setState] = useState<{
    html: string | null;
    problems: IContractTemplateProblem[];
    loading: boolean;
    failed: boolean;
  }>({ html: null, problems: EMPTY_PROBLEMS, loading: false, failed: false });

  const request = useCallback(
    async (html: string) => {
      if (!templateId) return;
      counter.current += 1;
      const mine = counter.current;
      setState((current) => ({ ...current, loading: true }));
      try {
        const { data } = await client.query<
          ContractTemplateDraftSampleQuery,
          ContractTemplateDraftSampleQueryVariables
        >({
          query: ContractTemplateDraftSampleDocument,
          variables: { id: templateId, html },
          fetchPolicy: 'no-cache',
        });
        if (mine !== counter.current) return;
        const payload = data?.contractTemplatePreviewDraft;
        setState({
          html: payload?.html ?? null,
          problems: payload?.problems ?? EMPTY_PROBLEMS,
          loading: false,
          failed: false,
        });
      } catch (caught) {
        if (mine !== counter.current) return;
        setState((current) => ({ ...current, loading: false, failed: true }));
      }
    },
    [client, templateId],
  );

  // Окно закрыли — поздний ответ уже ничего не пишет.
  useEffect(
    () => () => {
      counter.current += 1;
    },
    [],
  );

  return { ...state, request };
};

/** Все версии одного кода (история), новые сверху — порядок сервера. */
export const useContractTemplateVersions = (code: string | null) => {
  const canEdit = useCanEditContractTemplates();
  const { data, loading, error } = useContractTemplateListQuery({
    variables: { kind: null, includeInactive: true, code: code || null },
    skip: !code || !canEdit,
    fetchPolicy: 'cache-and-network',
  });
  return {
    versions: data?.contractTemplates ?? EMPTY_VERSIONS,
    loading,
    failed: Boolean(error),
  };
};

export interface IContractTemplateActionResult {
  ok: boolean;
  template: IContractTemplate | null;
  problems: IContractTemplateProblem[];
  errors: IEsignFieldError[];
  rerenderedDrafts: number;
}

interface IRawPayload {
  success?: boolean | null;
  customErrors?: Parameters<typeof toEsignErrors>[0];
  problems?: IContractTemplateProblem[] | null;
  template?: IContractTemplate | null;
  rerenderedDrafts?: number | null;
}

const toResult = (
  payload: IRawPayload | null | undefined,
): IContractTemplateActionResult => ({
  ok: Boolean(payload?.success),
  template: payload?.template ?? null,
  problems: payload?.problems ?? EMPTY_PROBLEMS,
  errors: toEsignErrors(payload?.customErrors ?? null),
  rerenderedDrafts: payload?.rerenderedDrafts ?? 0,
});

const FAILED: IContractTemplateActionResult = {
  ok: false,
  template: null,
  problems: EMPTY_PROBLEMS,
  errors: [],
  rerenderedDrafts: 0,
};

/** Текст ошибки проверки шаблона на языке интерфейса (+ подпись чипа). */
export const useContractTemplateProblemText = (
  tokens: IContractTemplateToken[] = EMPTY_TOKENS,
) => {
  const { t, i18n } = useTranslation();
  const language = templateEditorLanguage(i18n.language);
  return useCallback(
    (problem: {
      code: string;
      message?: string | null;
      token?: number | null;
    }) => {
      const base = problem.code
        ? t(`esign.contractTemplates.errors.${problem.code}`, {
            defaultValue: problem.message || problem.code,
          })
        : problem.message || '';
      const token =
        problem.token === null || problem.token === undefined
          ? undefined
          : tokens.find((item) => item.n === problem.token);
      return token
        ? t('esign.contractTemplates.errors.withChip', {
            text: base,
            chip: tokenLabel(token, language),
          })
        : base;
    },
    [language, t, tokens],
  );
};

/**
 * Записи редактора. Отказ проверки (`problems`) окно рисует само — тостом
 * только успех и сбой сети/прав.
 */
export const useContractTemplateActions = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const problemText = useContractTemplateProblemText();
  const [
    saveVersion,
    { loading: saving },
  ] = useStoreContractTemplateVersionMutation({
    update: evictTemplateCachesOnSuccess,
  });
  const [
    copyTemplate,
    { loading: creating },
  ] = useCopyContractTemplateMutation({ update: evictTemplateCachesOnSuccess });
  const [
    switchVersion,
    { loading: activating },
  ] = useSwitchContractTemplateVersionMutation({
    update: evictTemplateCachesOnSuccess,
  });
  const [
    toggleCode,
    { loading: toggling },
  ] = useToggleContractTemplateCodeMutation({
    update: evictTemplateCachesOnSuccess,
  });

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Esign,
      });
    },
    [addToast],
  );

  /** Брошенная ошибка: отказ по правам и сбой сети — разными текстами. */
  const crash = useCallback(
    (caught: unknown): IContractTemplateActionResult => {
      notify(
        ToastTypes.error,
        t('esign.contractTemplates.toast.failed'),
        caught instanceof ApolloError && isPermissionError(caught)
          ? t('esign.contractTemplates.errors.FORBIDDEN')
          : t('esign.contractTemplates.toast.network'),
      );
      return FAILED;
    },
    [notify, t],
  );

  /** Отказ без окна, где его нарисовать (список, история) — тостом. */
  const reportFailure = useCallback(
    (result: IContractTemplateActionResult) => {
      const lines = result.problems.length
        ? result.problems.map(problemText)
        : result.errors.map(problemText);
      notify(
        ToastTypes.error,
        t('esign.contractTemplates.toast.failed'),
        lines.filter(Boolean).join('\n') || undefined,
      );
    },
    [notify, problemText, t],
  );

  const save = useCallback(
    async (
      templateId: string,
      html: string,
      tokens: IContractTemplateToken[],
      comment: string,
    ): Promise<IContractTemplateActionResult> => {
      try {
        const { data } = await saveVersion({
          variables: {
            id: templateId,
            html,
            tokens: JSON.stringify(tokens),
            comment: comment.trim() || null,
          },
        });
        const result = toResult(data?.saveContractTemplateVersion);
        if (result.ok) {
          notify(
            ToastTypes.success,
            t('esign.contractTemplates.toast.saved', {
              version: result.template?.version ?? '',
            }),
            t('esign.contractTemplates.toast.rerendered', {
              n: result.rerenderedDrafts,
            }),
          );
        }
        return result;
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, notify, saveVersion, t],
  );

  const create = useCallback(
    async (input: {
      basedOnId: string;
      name: string;
      code?: string;
      legalEntityId?: string | null;
      appendix?: string | null;
    }): Promise<IContractTemplateActionResult> => {
      try {
        const { data } = await copyTemplate({
          variables: {
            basedOnId: input.basedOnId,
            name: input.name.trim(),
            code: input.code?.trim() || null,
            legalEntityId: input.legalEntityId || null,
            appendix: input.appendix || null,
          },
        });
        const result = toResult(data?.createContractTemplate);
        if (result.ok) {
          notify(
            ToastTypes.success,
            t('esign.contractTemplates.toast.created', {
              name: result.template?.name ?? input.name,
            }),
          );
        }
        return result;
      } catch (caught) {
        return crash(caught);
      }
    },
    [copyTemplate, crash, notify, t],
  );

  const activate = useCallback(
    async (templateId: string): Promise<IContractTemplateActionResult> => {
      try {
        const { data } = await switchVersion({
          variables: { id: templateId },
        });
        const result = toResult(data?.activateContractTemplateVersion);
        if (result.ok) {
          notify(
            ToastTypes.success,
            t('esign.contractTemplates.toast.activated', {
              version: result.template?.version ?? '',
            }),
            t('esign.contractTemplates.toast.rerendered', {
              n: result.rerenderedDrafts,
            }),
          );
        } else {
          reportFailure(result);
        }
        return result;
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, notify, reportFailure, switchVersion, t],
  );

  const setCodeActive = useCallback(
    async (
      code: string,
      active: boolean,
    ): Promise<IContractTemplateActionResult> => {
      try {
        const { data } = await toggleCode({ variables: { code, active } });
        const result = toResult(data?.setContractTemplateActive);
        if (result.ok) {
          notify(
            ToastTypes.success,
            t(
              active
                ? 'esign.contractTemplates.toast.enabled'
                : 'esign.contractTemplates.toast.disabled',
            ),
          );
        } else {
          reportFailure(result);
        }
        return result;
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, notify, reportFailure, t, toggleCode],
  );

  return {
    save,
    create,
    activate,
    setCodeActive,
    busy: { saving, creating, activating, toggling },
  };
};
